import { LitElement, html } from 'lit';

import { VPVStoreController } from './vpv-store-controller.js';

/**
 * <decked-fitment-table>
 *
 * Builds the fitment guide tables (decked.com/pages/fitment) from the vehicle picker data file
 * instead of a hand maintained table in the page content.
 *
 * One row per drawer system variant, grouped into category > make tables.
 * CargoGlide / Tool Box columns link to those PDPs pre filtered to the vehicle when any vehicle
 * on that row has a compatible product in the data, otherwise "N/A".
 *
 * Attributes:
 *   drawers-url: PDP used for the Drawer System SKU links
 *   cargoglide-url: PDP used for the CargoGlide "See Options" links
 *   tool-box-url: PDP used for the Tool Box "See Options" links
 */

// Order here is the order the categories render in. First match wins in categorize().
export const CATEGORIES = [
  { key: 'full-size', title: 'Full-Size Trucks', sizeLabel: 'Bed Length' },
  { key: 'midsize', title: 'Midsize Trucks', sizeLabel: 'Bed Length' },
  { key: 'service-body', title: 'Service Body', sizeLabel: 'Wheelbase' },
  { key: 'cargo-van', title: 'Cargo Vans', sizeLabel: 'Wheelbase' },
  { key: 'suv', title: 'SUVs', sizeLabel: 'Model' },
];

// The data file doesn't have a vehicle class, so midsize and SUV are matched on model name.
// Anything that isn't a van, service body, midsize or SUV falls into full size.
const MIDSIZE_MODELS = /ranger|maverick|gladiator|colorado|canyon|tacoma|frontier|rivian|r1t|ridgeline|santa cruz/i;
const SUV_MODELS = /bronco|4runner|sequoia|land cruiser|tahoe|suburban|yukon|expedition|wrangler|cherokee|explorer|\bsuv\b/i;

// Service body is one table for every make
const SERVICE_BODY_GROUP = 'Service Body - All Models';

// Makes that share a table on the fitment page
const MAKE_GROUPS = {
  Chevy: 'Chevy/GMC',
  Chevrolet: 'Chevy/GMC',
  GMC: 'Chevy/GMC',
  'Jeep Trucks': 'Jeep',
  'Mercedes Benz (all Sprinters)': 'Sprinter',
  'Sprinter Van': 'Sprinter',
};

// Make names as they read in the model column
const MAKE_DISPLAY = {
  'Jeep Trucks': 'Jeep',
  'Mercedes Benz (all Sprinters)': 'Mercedes Benz',
  'Sprinter Van': 'Mercedes Benz',
};

// Make tables render in this order, anything else goes after, alphabetical
const MAKE_ORDER = ['Ford', 'Chevy/GMC', 'Ram', 'Toyota', 'Nissan', 'Jeep', 'Rivian', 'Sprinter'];

// Drawer system product types, "Decked" is what the older data files used
const DRAWER_TYPES = ['Drawers', 'Drawers with Load Floor', 'Decked'];
const isDrawer = product => DRAWER_TYPES.includes(product?.productType);
const isCargoGlide = product => /^cargoglide/i.test(product?.productType || '');
const isToolBox = product => /tool box/i.test(product?.productType || '');

export function categorize(vehicle) {
  const model = vehicle.model || '';
  if (/service body/i.test(model)) return 'service-body';
  if (/van/i.test(model)) return 'cargo-van';
  if (SUV_MODELS.test(model)) return 'suv';
  if (MIDSIZE_MODELS.test(model)) return 'midsize';
  return 'full-size';
}

export function makeGroup(make) {
  return MAKE_GROUPS[make] || make;
}

// 5'6" -> 5' 6", 136" Wheelbase -> 136", Service Body (48"-50" wide only) -> 48"-50" wide only
export function formatSize(size) {
  return (size || '')
    .replace(/\s*wheelbase/i, '')
    .replace(/^service body\s*\((.*)\)$/i, '$1')
    .replace(/(\d+)'\s*(\d+)"/, `$1' $2"`)
    .trim();
}

// "2007-2018 AND 2019 OLD BODY STYLE" -> "2007-2018 and 2019 Old Body Style", "2015-Current" -> "2015-current"
export function formatYears(years) {
  let formatted = (years || '').trim();
  if (formatted == formatted.toUpperCase()) {
    formatted = formatted.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
  }
  return formatted.replace(/\band\b/gi, 'and').replace(/\bcurrent\b/gi, 'current');
}

// Fallback when a drawer variant title isn't "years / size"
export function yearRange(years) {
  const nums = years.map(Number).filter(Boolean);
  if (!nums.length) return '';
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (max >= new Date().getFullYear()) return `${min}-current`;
  return min == max ? `${min}` : `${min}-${max}`;
}

// Drawer variant titles look like "2015-Current / 5' 6"", the years part is hand written
// and has the extra notes we want to show, ex: "1999-2006 and 2007 Classic Edition"
export function parseDrawerTitle(title, vehicles) {
  const [yearsPart, ...sizeParts] = (title || '').split(' / ');
  const first = vehicles[0] || {};
  const hasYears = /\d{4}/.test(yearsPart);
  const size = sizeParts.length ? sizeParts.join(' / ') : first.bed_length || (first.body_style != first.year ? first.body_style : '');
  return {
    years: hasYears ? formatYears(yearsPart) : yearRange(vehicles.map(v => v.year)),
    size: formatSize(size),
  };
}

const pdpLink = (base, vehicle) => {
  if (!base || !vehicle) return null;
  const u = new URL(base, window.location.origin);
  u.searchParams.set('make', vehicle.make);
  u.searchParams.set('model', vehicle.model);
  u.searchParams.set('year', vehicle.year);
  return u.pathname + u.search;
};

const latestVehicle = vehicles => vehicles.reduce((latest, v) => (!latest || Number(v.year) > Number(latest.year) ? v : latest), null);

/**
 * vehicles: rows from the vp data file, products as ids (allProducts) or objects (products)
 * variants: map of variant id -> product object from the data file
 * skus: map of variant id -> sku for anything missing a sku in the data file
 */
export function buildFitment(vehicles, variants, skus = {}, urls = {}) {
  // drawer variant id -> row
  const rows = new Map();

  for (const vehicle of vehicles) {
    const products = (vehicle.allProducts || vehicle.products || []).map(p => (typeof p === 'string' ? variants[p] : p)).filter(Boolean);

    for (const drawer of products.filter(isDrawer)) {
      if (!rows.has(drawer.id)) {
        rows.set(drawer.id, {
          drawer,
          category: categorize(vehicle),
          group: categorize(vehicle) == 'service-body' ? SERVICE_BODY_GROUP : makeGroup(vehicle.make),
          vehicles: [],
          cargoGlideVehicles: [],
          toolBoxVehicles: [],
        });
      }
      const row = rows.get(drawer.id);
      row.vehicles.push(vehicle);
      if (products.some(isCargoGlide)) row.cargoGlideVehicles.push(vehicle);
      if (products.some(isToolBox)) row.toolBoxVehicles.push(vehicle);
    }
  }

  // Rows with the same SKU and size in the same table get merged, ex: Silverado 1500 + Sierra 1500
  const merged = new Map();
  for (const row of rows.values()) {
    const sku = row.drawer.sku || skus[row.drawer.id] || '';
    const { years, size } = parseDrawerTitle(row.drawer.title, row.vehicles);
    const key = [row.category, row.group, sku || row.drawer.id, size].join('|');

    if (!merged.has(key)) {
      merged.set(key, { ...row, sku, size, years, vehicles: [], cargoGlideVehicles: [], toolBoxVehicles: [] });
    }
    const m = merged.get(key);
    m.vehicles.push(...row.vehicles);
    m.cargoGlideVehicles.push(...row.cargoGlideVehicles);
    m.toolBoxVehicles.push(...row.toolBoxVehicles);
  }

  const categories = CATEGORIES.map(category => ({ ...category, tables: new Map() }));
  const byKey = Object.fromEntries(categories.map(c => [c.key, c]));

  for (const row of merged.values()) {
    // body style is left out, the drawer variant title years already cover it (Classic Edition, Old Body Style, etc)
    const models = [];
    for (const v of row.vehicles) {
      const make = MAKE_DISPLAY[v.make] || v.make;
      const name = row.category == 'service-body' ? make : `${make} ${v.model}`;
      if (!models.includes(name)) models.push(name);
    }

    const drawerVehicle = latestVehicle(row.vehicles);
    const display = {
      name: models.sort().join(' / '),
      model: `${models.join(' / ')}${row.years ? ` (${row.years})` : ''}`,
      size: row.size,
      sku: row.sku,
      skuLink: pdpLink(urls.drawers, drawerVehicle),
      firstYear: Math.min(...row.vehicles.map(v => Number(v.year) || 9999)),
      cargoGlideLink: row.cargoGlideVehicles.length ? pdpLink(urls.cargoGlide, latestVehicle(row.cargoGlideVehicles)) : null,
      toolBoxLink: row.toolBoxVehicles.length ? pdpLink(urls.toolBox, latestVehicle(row.toolBoxVehicles)) : null,
    };

    const tables = byKey[row.category].tables;
    if (!tables.has(row.group)) tables.set(row.group, []);
    tables.get(row.group).push(display);
  }

  const makeIndex = name => {
    const i = MAKE_ORDER.indexOf(name);
    return i == -1 ? MAKE_ORDER.length : i;
  };

  return categories
    .map(category => ({
      ...category,
      tables: [...category.tables.entries()]
        .sort(([a], [b]) => makeIndex(a) - makeIndex(b) || a.localeCompare(b))
        .map(([make, rows]) => ({
          make,
          rows: rows.sort((a, b) => a.name.localeCompare(b.name) || a.firstYear - b.firstYear || a.size.localeCompare(b.size, undefined, { numeric: true })),
        })),
    }))
    .filter(category => category.tables.length);
}

class FitmentTable extends LitElement {
  static properties = {
    drawersUrl: { type: String, attribute: 'drawers-url' },
    cargoGlideUrl: { type: String, attribute: 'cargoglide-url' },
    toolBoxUrl: { type: String, attribute: 'tool-box-url' },
    categories: { type: Array, state: true },
    skus: { type: Object, state: true },
  };

  constructor() {
    super();
    this.drawersUrl = '/products/drawers';
    this.cargoGlideUrl = '/products/cargoglide-1000';
    this.toolBoxUrl = '/products/decked-tool-box';
    this.categories = null;
    this.skus = {};
  }

  _vehicleJsonReadyState = new VPVStoreController(
    this,
    s => s.vehicleJsonReady,
    isReady => {
      if (isReady) this.collectFitmentData();
    }
  );

  connectedCallback() {
    this.innerHTML = '';
    super.connectedCallback();

    // main.js runs before static.vehicle-data.js, so VehicleData might not exist yet
    if (window.VehicleData) {
      this.initVehicleData();
    } else if (document.readyState == 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initVehicleData(), { once: true });
    } else {
      this.initVehicleData();
    }
  }

  initVehicleData() {
    if (!window.VehicleData) {
      console.error('[fitment-table] VehicleData is not defined');
      return;
    }
    window.VehicleData.init();
    // Handle already-loaded case, store watch won't fire for pre-existing values
    if (window.__VPV?.vehicleJsonReady) {
      this.collectFitmentData();
    }
  }

  async collectFitmentData() {
    // initVehicleData and the store watch can both call this, only build once
    if (this.categories || this._collecting) return;
    this._collecting = true;

    const vehicles = window.__VPV?.vehicleData?.allFormatted || [];
    const variants = window.VehicleData?.variantObjects || {};

    // The data file doesn't always have skus, grab any missing drawer skus from the storefront api
    const missing = Object.values(variants)
      .filter(product => isDrawer(product) && !product.sku)
      .map(product => product.id);
    if (missing.length) {
      const skus = {};
      for (let i = 0; i < missing.length; i += 100) {
        const { variants: fetched = [] } = (await window.VehicleData.fetchProductDataByIDs(missing.slice(i, i + 100))) || {};
        for (const variant of fetched) {
          skus[variant.gid] = variant.sku;
        }
      }
      this.skus = skus;
    }

    try {
      this.categories = buildFitment(vehicles, variants, this.skus, { drawers: this.drawersUrl, cargoGlide: this.cargoGlideUrl, toolBox: this.toolBoxUrl });
    } catch (e) {
      console.error('[fitment-table] buildFitment failed', e);
      this.categories = [];
    }
  }

  renderLink(link, text = 'See Options') {
    return link ? html`<a href="${link}">${text}</a>` : 'N/A';
  }

  renderTable(category, table) {
    // plain h4 + table, same markup as the old tables in the page content so .prose styles them the same
    return html`
      <table>
        <tbody>
          <tr>
            <th>${table.make}</th>
            <th>${category.sizeLabel}</th>
            <th>Drawer System SKU</th>
            <th>CargoGlide</th>
            <th>Tool Box</th>
          </tr>
          ${table.rows.map(
            row => html`
              <tr>
                <td>${row.model}</td>
                <td>${row.size}</td>
                <td>${row.sku ? this.renderLink(row.skuLink, row.sku) : ''}</td>
                <td>${this.renderLink(row.cargoGlideLink)}</td>
                <td>${this.renderLink(row.toolBoxLink)}</td>
              </tr>
            `
          )}
        </tbody>
      </table>
    `;
  }

  render() {
    if (!this.categories) {
      return html`<p class="fitment-table__loading text-center">Loading fitment guide...</p>`;
    }

    if (!this.categories.length) {
      return html`<p class="text-center">No fitment data found.</p>`;
    }

    return html`
      ${this.categories.map(
        category => html`
          <h4 id="fitment-${category.key}">${category.title.toUpperCase()}</h4>
          ${category.tables.map(table => this.renderTable(category, table))}
        `
      )}
    `;
  }

  createRenderRoot() {
    return this;
  }
}

if (!window.customElements.get('decked-fitment-table')) {
  customElements.define('decked-fitment-table', FitmentTable);
}
